<script setup lang="ts">
import { Building2, Home, ArrowUpRight } from 'lucide-vue-next';
import { cn } from '@/lib/utils';

const props = defineProps<{
    project: any;
}>();

const emit = defineEmits(['open']);
</script>

<template>
  <div 
    tabindex="0"
    role="listitem"
    :aria-label="`Ouvrir le projet ${project.name} par ${project.developer}. ${project.apartmentCount} plans disponibles.`"
    @click="$emit('open', project)"
    @keydown.enter="$emit('open', project)"
    class="group bg-slate-900/40 backdrop-blur-sm border border-slate-800 rounded-[2rem] p-8 hover:border-blue-500/30 transition-all duration-500 hover:shadow-2xl hover:shadow-blue-500/5 relative overflow-hidden flex flex-col cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500/50"
  >
    <!-- Card Decoration -->
    <div class="absolute top-0 right-0 w-32 h-32 bg-blue-500/5 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
    
    <div class="flex items-start justify-between mb-8">
      <div class="w-14 h-14 bg-slate-950 border border-slate-800 rounded-2xl flex items-center justify-center shadow-inner group-hover:scale-110 transition-transform duration-500 overflow-hidden">
         <Building2 v-if="project.developer === 'Cogedim'" class="w-8 h-8 text-blue-500/40 group-hover:text-blue-500 transition-colors" />
         <Home v-else class="w-8 h-8 text-slate-700 group-hover:text-blue-400 transition-colors" />
      </div>
      <div class="flex flex-col items-end gap-2">
        <span class="text-[10px] font-black text-blue-500 uppercase bg-blue-500/10 px-2 py-0.5 rounded-md">{{ project.developer }}</span>
        <span v-if="project.newCount > 0" class="text-[8px] font-black text-emerald-500 uppercase bg-emerald-500/10 px-2 py-0.5 rounded-md animate-pulse">
          {{ project.newCount }} Nouveau{{ project.newCount > 1 ? 's' : '' }}
        </span>
      </div>
    </div>

    <div class="flex-1 space-y-2">
      <h3 class="text-lg font-black text-white tracking-widest leading-tight uppercase group-hover:text-blue-400 transition-colors">{{ project.name }}</h3>
      <div class="flex items-center gap-2 opacity-40">
          <span class="h-px w-4 bg-slate-500"></span>
          <span class="text-[8px] font-mono tracking-widest">ID: {{ project.id.toString().padStart(4, '0') }}</span>
      </div>
    </div>

    <div class="mt-10 pt-6 border-t border-slate-800/50 flex items-center justify-between">
       <div class="flex flex-col">
          <span class="text-[9px] font-black text-slate-600 uppercase tracking-[0.2em]">Plans disponibles</span>
          <span :class="cn('text-xl font-black mt-1', project.apartmentCount > 0 ? 'text-white' : 'text-slate-700')">{{ project.apartmentCount }}</span>
       </div>
       <button 
         @click.stop="$emit('open', project)"
         aria-label="Ouvrir les détails"
         class="w-12 h-12 bg-slate-950 border border-slate-800 rounded-2xl flex items-center justify-center hover:bg-blue-600 hover:border-blue-500 text-slate-500 hover:text-white transition-all shadow-xl active:scale-90"
       >
          <ArrowUpRight class="w-5 h-5" aria-hidden="true" />
       </button>
    </div>

    <!-- Hover Bar -->
    <div class="absolute bottom-0 left-0 w-full h-1 bg-blue-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
  </div>
</template>
